import React from "react";
import { ArrowRight, Phone, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RESTAURANT, IMAGES } from "@/lib/content";
import { useSiteContent } from "@/lib/site-content";

export default function Hero() {
  const { content } = useSiteContent();
  const h = content.hero;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-10 md:pt-14 pb-16 md:pb-24"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-stretch">
          <div className="lg:col-span-7 flex flex-col justify-between rounded-3xl bg-[#F5ECE1] p-8 md:p-12 lg:p-14 relative overflow-hidden grain rise">
            <div className="flex items-center gap-4 z-10">
              <img
                src={RESTAURANT.logo}
                alt="Thai Noodle & Rice logo"
                data-testid="hero-logo"
                className="h-16 w-16 md:h-20 md:w-20 rounded-2xl object-cover border border-[#E8DED5] shadow-[0_12px_32px_-14px_rgba(26,26,26,0.4)]"
              />
              <div className="flex flex-col gap-2">
                <span className="chip" data-testid="hero-chip">
                  <span className="h-2 w-2 rounded-full bg-[#E53935]" />
                  {h.chip}
                </span>
                <span className="text-xs tracking-[0.25em] uppercase text-[#5C534D] font-bold">
                  {h.eyebrow}
                </span>
              </div>
            </div>

            <div className="mt-10 z-10">
              <h1
                data-testid="hero-headline"
                className="font-display font-black tracking-tighter leading-[0.92] text-[56px] sm:text-[72px] lg:text-[92px]"
              >
                {h.headline_line1} <br />
                <span className="italic font-light">{h.headline_line2_italic}</span>
                <br />
                <span className="text-[#E53935]">{h.headline_line3_red}</span>{" "}
                {h.headline_line4}
              </h1>
              <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-[#5C534D]">
                {h.subhead}{" "}
                <span className="text-[#1A1A1A] font-semibold">{h.tagline_th}</span>
              </p>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4 z-10">
              <Button
                asChild
                data-testid="hero-cta-menu"
                className="rounded-full bg-[#E53935] hover:bg-[#C62828] text-white h-14 px-8 text-base font-semibold group"
              >
                <a href="#menu" className="inline-flex items-center gap-2">
                  {h.cta_primary_label}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-cta-call"
                className="rounded-full bg-white hover:bg-[#1A1A1A] hover:text-white border-2 border-[#1A1A1A] h-14 px-7 text-base font-semibold transition-colors"
              >
                <a
                  href={`tel:${(content.contact.phone || "").replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-2"
                >
                  <Phone className="h-5 w-5" />
                  {content.contact.phone}
                </a>
              </Button>
            </div>

            <div className="mt-10 flex items-center gap-6 z-10">
              <div className="flex items-center -space-x-2">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-9 w-9 rounded-full border-2 border-[#F5ECE1] bg-gradient-to-br from-[#E53935] to-[#F5A623] grid place-items-center text-white text-xs font-black"
                  >
                    {"★"}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-[#E53935]">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="h-4 w-4 fill-[#E53935]" />
                  ))}
                </div>
                <p className="text-xs text-[#5C534D] mt-1">
                  Loved by Manchester · 4.6★ on Google
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-rows-5 gap-6">
            <div className="row-span-3 relative rounded-3xl overflow-hidden group">
              <img
                src={IMAGES.hero}
                alt="Bangkok street market"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="text-white/80 text-[10px] tracking-[0.3em] uppercase font-bold">
                  {h.price_tag}
                </div>
                <div className="text-white font-display text-2xl font-bold mt-1">
                  {h.today_caption}
                </div>
              </div>
            </div>
            <div className="row-span-2 grid grid-cols-2 gap-6">
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src={IMAGES.padThai}
                  alt="Pad Thai"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div className="rounded-3xl bg-[#1A1A1A] text-white p-6 flex flex-col justify-between">
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#F5A623]">
                  Today
                </div>
                <div>
                  <div className="font-display font-black text-3xl leading-none">
                    {h.hours_chip_number}
                  </div>
                  <div className="text-xs text-white/70 mt-2">
                    {h.hours_chip_sub}
                  </div>
                </div>
                <a
                  href="#reserve"
                  data-testid="hero-mini-reserve"
                  className="text-sm font-semibold inline-flex items-center gap-1 hover:text-[#F5A623] transition-colors"
                >
                  Reserve <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
